import React from 'react';
import { useSelector } from 'react-redux';
import { Routes, Route, Navigate } from 'react-router-dom-v5-compat';

import Page from '../../components/page';
import InventoryPage from './inventory';
import { ExternalInventoryPage } from './inventory/external-inventory/external-inventory-source-page';
import LeftNavContainer from './left-nav-container';
import PresetGroupsPage from './preset-groups/preset-groups-page';
import ProductsPage from './products';
import { PurchaseOrdersListPage } from './purchase-orders/purchase-orders-page';
import { PurchaseOrdersLayout } from './purchase-orders/purchase-orders-layout';
import ReportingCategoriesPage from './reporting-categories/reporting-categories-page';
import { SuppliersPage } from './suppliers/suppliers-page';
import ToggleLeftNav from '../../components/toggle-left-nav';
import { useInventoryManagementBasicFeatureGate } from '../../feature-gates/use-inventory-management-basic-feature-gate';
import { FiltersContextProvider } from '../../filters/context';
import Global from '../../store/global-selectors';
import { UserHasRoleAndSubscription } from '../../role-based-route';
import { useProductCatalogFeatureGate } from '../../feature-gates/use-product-catalog-feature-gate';
import { UserPermissionService } from '../../services/user-permission-service';
import {
  PRODUCT_TYPE_PATH,
  CATEGORIES_PATH,
  INVENTORY_WAREHOUSE_PATH,
  INVENTORY_PATH,
  PURCHASE_ORDER_DETAILS_PATH,
  PURCHASE_ORDERS_PATH,
  BASE_PRODUCTS_PATH,
  EXTERNAL_INVENTORY_PATH,
  EXTERNAL_INVENTORY_SOURCE_PATH,
  SUPPLIERS_PATH,
  SUPPLIERS_DETAILS_PATH,
  TRANSFER_ORDERS_PATH,
  TRANSFER_ORDER_DETAILS_PATH,
} from '../../routes/constants';
import { useEnableExternalInventory } from '../../feature-flags/hooks/use-enable-external-inventory';
import style from '../grid-page.module.less';
import { PurchaseOrderDetailPage } from './purchase-orders/purchase-order-page/purchase-order-page';
import { SearchContextProvider } from './quicksearch-context';
import { ExternalInventoryConnectionsProvider } from './external-inventory-connections-context';
import { SupplierDetailPage } from './suppliers/supplier-page/supplier-page';
import { SuppliersLayout } from './suppliers/suppliers-layout';
import { TransferOrdersLayout } from './transfer-orders/components/transfer-orders-layout/transfer-orders-layout';
import { TransferOrdersListPage } from './transfer-orders/components/transfer-orders-list-page/transfer-orders-list-page';
import { TransferOrderDetailPage } from './transfer-orders/transfer-order-page/transfer-order-page';
import { useEnableTransferOrders } from '../../feature-flags/hooks/use-enable-transfer-orders';

const ProductsPageRedirect = ({ isProductCatalogEnabled, isInventoryManagementEnabled }) => {
  const userRoles = useSelector(Global.selectUseruserRoles);

  if (isProductCatalogEnabled) {
    return <Navigate replace to={BASE_PRODUCTS_PATH} />;
  }

  if (isInventoryManagementEnabled) {
    return <Navigate replace to={`${BASE_PRODUCTS_PATH}${INVENTORY_PATH}`} />;
  }

  return <Navigate replace to={UserPermissionService.getFirstPermissableRouteUrl(userRoles)} />;
};

export const ProductsPageRouter = () => {
  const { isFeatureEnabled: isProductCatalogEnabled } = useProductCatalogFeatureGate();
  const { isFeatureEnabled: isInventoryManagementEnabled } = useInventoryManagementBasicFeatureGate();
  const isExternalInventoryEnabled = useEnableExternalInventory();
  const isTransferOrdersEnabled = useEnableTransferOrders();
  return (
    <Page>
      <FiltersContextProvider>
        <ExternalInventoryConnectionsProvider>
          <SearchContextProvider>
            <div className={style['grid-page']}>
              <ToggleLeftNav>
                <LeftNavContainer />
              </ToggleLeftNav>
              <Routes>
                {isProductCatalogEnabled && (
                  <>
                    <Route
                      element={
                        <UserHasRoleAndSubscription>
                          <PresetGroupsPage />
                        </UserHasRoleAndSubscription>
                      }
                      path={PRODUCT_TYPE_PATH}
                    />
                    <Route
                      element={
                        <UserHasRoleAndSubscription>
                          <ReportingCategoriesPage />
                        </UserHasRoleAndSubscription>
                      }
                      path={CATEGORIES_PATH}
                    />
                    <Route
                      element={
                        <UserHasRoleAndSubscription>
                          <ProductsPage />
                        </UserHasRoleAndSubscription>
                      }
                      path='/'
                    />
                  </>
                )}

                {isInventoryManagementEnabled && (
                  <>
                    {/* Two inventory components/routes are here because we're at a V6 refactor midpoint. Optional segments/parameters are in the latest, but we need to move to a higher version than the refactor can support to use them.
                    https://stackoverflow.com/questions/70005601/alternate-way-for-optional-parameters-in-v6
                   */}
                    <Route
                      element={
                        <UserHasRoleAndSubscription>
                          <InventoryPage />
                        </UserHasRoleAndSubscription>
                      }
                      path={INVENTORY_WAREHOUSE_PATH}
                    />
                    <Route
                      element={
                        <UserHasRoleAndSubscription>
                          <InventoryPage />
                        </UserHasRoleAndSubscription>
                      }
                      path={INVENTORY_PATH}
                    />
                    {isExternalInventoryEnabled && (
                      <>
                        <Route
                          element={
                            <UserHasRoleAndSubscription>
                              <ExternalInventoryPage />
                            </UserHasRoleAndSubscription>
                          }
                          path={EXTERNAL_INVENTORY_PATH}
                        />
                        <Route
                          element={
                            <UserHasRoleAndSubscription>
                              <ExternalInventoryPage />
                            </UserHasRoleAndSubscription>
                          }
                          path={EXTERNAL_INVENTORY_SOURCE_PATH}
                        />
                      </>
                    )}
                  </>
                )}
                <Route element={<PurchaseOrdersLayout />} path={PURCHASE_ORDERS_PATH}>
                  <Route element={<PurchaseOrdersListPage />} index />
                  <Route element={<PurchaseOrderDetailPage />} path={PURCHASE_ORDER_DETAILS_PATH} />
                </Route>
                <Route element={<SuppliersLayout />} path={SUPPLIERS_PATH}>
                  <Route element={<SuppliersPage />} index />
                  <Route element={<SupplierDetailPage />} path={SUPPLIERS_DETAILS_PATH} />
                </Route>
                {isTransferOrdersEnabled && (
                  <Route element={<TransferOrdersLayout />} path={TRANSFER_ORDERS_PATH}>
                    <Route element={<TransferOrdersListPage />} index />
                    <Route element={<TransferOrderDetailPage />} path={TRANSFER_ORDER_DETAILS_PATH} />
                  </Route>
                )}
                <Route
                  element={
                    <ProductsPageRedirect
                      isInventoryManagementEnabled={isInventoryManagementEnabled}
                      isProductCatalogEnabled={isProductCatalogEnabled}
                    />
                  }
                  path='*'
                />
              </Routes>
            </div>
          </SearchContextProvider>
        </ExternalInventoryConnectionsProvider>
      </FiltersContextProvider>
    </Page>
  );
};
